class SubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.productID = null;
    this.variantID = null;
    this.excludedVariants = [];
    this.product = null;
    this.selectedFrequency = 30;
    this.sellingPlans = [];
    this.currentVariant = null;
  }

  connectedCallback() {
    this.initializeWidget();
  }

  initializeWidget() {
    // Hide old Recharge widget immediately to prevent flash
    this.hideOldWidget();

    // Get data attributes
    this.productID = this.getAttribute('data-product-id');
    this.variantID = Number(this.getAttribute('data-variant-id'));
    const excludedVariantsStr = this.getAttribute('data-excluded-variants');

    if (excludedVariantsStr) {
      this.excludedVariants = excludedVariantsStr.split(',').map(id => Number(id.trim())).filter(Boolean);
    }

    // Check if current variant is excluded
    if (this.isVariantExcluded(this.variantID)) {
      console.log('Current variant is excluded from subscriptions');
      this.hideWidget();
      return;
    }

    // Load product data from native Shopify JSON (no API call needed!)
    this.product = this.loadNativeProductData();

    if (this.product && this.product.selling_plans && this.product.selling_plans.length > 0) {
      this.renderWidget();
      this.attachEventListeners();
    } else {
      // No selling plans available - hide the widget
      this.hideWidget();
    }
  }

  /**
   * Load product selling plan data from the native Shopify JSON embedded in the page.
   * This eliminates the need for the slow Recharge API call.
   */
  loadNativeProductData() {
    const dataScript = document.getElementById(`selling-plans-${this.productID}`);
    if (!dataScript) {
      console.warn('Native selling plans data not found for product:', this.productID);
      return null;
    }

    try {
      return JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('Error parsing native selling plans data:', error);
      return null;
    }
  }

  showLoadingSkeleton() {
    const widgetContainer = this.querySelector('.subscription-widget');
    if (widgetContainer) {
      widgetContainer.classList.remove('hidden');
      widgetContainer.classList.add('loading');
    }
  }

  hideLoadingSkeleton() {
    const widgetContainer = this.querySelector('.subscription-widget');
    if (widgetContainer) {
      widgetContainer.classList.remove('loading');
    }
  }

  hideOldWidget() {
    // Hide the old rc-widget if it exists
    const oldWidget = document.querySelector('recharge-widget .widget');
    if (oldWidget) {
      oldWidget.style.display = 'none';
    }

    // Also hide the entire recharge-widget custom element
    const rechargeWidget = document.querySelector('recharge-widget');
    if (rechargeWidget) {
      rechargeWidget.style.display = 'none';
    }

    // Hide any Recharge container wrappers and widgets
    const rechargeContainers = document.querySelectorAll(
      '.rc-container-wrapper, .rc_container_wrapper, .rc-container, .rc_container, ' +
      '.rc-widget, .rc_widget, .rc-template, .rc_popup, ' +
      '[data-widget-container-wrapper], [data-widget-container], [data-widget], [data-template]'
    );
    rechargeContainers.forEach(container => {
      container.style.display = 'none';
    });

    // Hide any app block widgets (Recharge app blocks)
    const appBlocks = document.querySelectorAll('[id*="shopify-block"][id*="recharge"], .recharge-widget-wrapper, #rc-widget, [class*="rc-widget"]');
    appBlocks.forEach(block => {
      block.style.display = 'none';
    });

    // IMPORTANT: Disable all Recharge widget form inputs to prevent them from being submitted
    // This prevents the old widget from interfering with cart submissions
    const rechargeInputs = document.querySelectorAll(
      'recharge-widget input, recharge-widget select, ' +
      '.rc-widget input, .rc-widget select, ' +
      '[class*="rc-widget"] input, [class*="rc-widget"] select, ' +
      'input[name*="selling_plan_"], select[name*="rc_selling_plan"]'
    );
    rechargeInputs.forEach(input => {
      input.disabled = true;
      input.removeAttribute('name'); // Remove name so it won't be submitted
    });
  }

  hideWidget() {
    const widgetContainer = this.querySelector('.subscription-widget');
    if (widgetContainer) {
      widgetContainer.classList.add('hidden');
    }
  }

  isVariantExcluded(variantId) {
    return this.excludedVariants.includes(Number(variantId));
  }

  renderWidget() {
    const widgetContainer = this.querySelector('.subscription-widget');

    if (widgetContainer) {
      widgetContainer.classList.remove('hidden');

      // Get current variant data
      this.currentVariant = this.product.variants.find(v => v.id === this.variantID);

      // Get selling plans for current variant
      this.updateSellingPlans();

      // Detect if this is a complex product (multiple variants with Servings options)
      const isComplexProduct = this.detectComplexProduct();

      // Hide/show frequency buttons for subscription based on product complexity
      const onetimeDetailsContainer = this.querySelector('.subscription-widget__onetime-details');

      if (!isComplexProduct) {
        const frequenciesContainer = this.querySelector('.subscription-widget__frequencies');
        if (frequenciesContainer) {
          frequenciesContainer.style.display = 'none';
        }
        const onetimeFrequenciesContainer = this.querySelector('.subscription-widget__onetime-frequencies');
        if (onetimeFrequenciesContainer) {
          onetimeFrequenciesContainer.style.display = 'none';
        }
        // Add class to hide the entire one-time details section
        if (onetimeDetailsContainer) {
          onetimeDetailsContainer.classList.add('hidden-content');
        }
      } else {
        // Show frequency buttons for complex products (both subscription and one-time)
        const frequenciesContainer = this.querySelector('.subscription-widget__frequencies');
        if (frequenciesContainer) {
          frequenciesContainer.style.display = 'flex';
        }
        const onetimeFrequenciesContainer = this.querySelector('.subscription-widget__onetime-frequencies');
        if (onetimeFrequenciesContainer) {
          onetimeFrequenciesContainer.style.display = 'flex';
        }
        // Remove hidden class from one-time details section
        if (onetimeDetailsContainer) {
          onetimeDetailsContainer.classList.remove('hidden-content');
        }
        // Update one-time frequency buttons with variant data
        this.updateOnetimeFrequencyButtons();
      }

      // Update prices
      this.updateSubscriptionPrice();
      this.updateOnetimePrice();

      // Update main submit button with price
      this.updateMainSubmitButton();

      // Set default frequency based on first selling plan
      if (this.sellingPlans.length > 0) {
        const firstPlan = this.sellingPlans[0];
        this.selectedFrequency = firstPlan.order_interval_frequency;
        this.updateActiveFrequencyButton();

        // Ensure subscription radio is checked by default
        const subscriptionRadio = this.querySelector('.subscription-widget__subscription-radio');
        if (subscriptionRadio) {
          subscriptionRadio.checked = true;
        }

        // For simple products, set the default selling plan immediately
        if (!isComplexProduct) {
          this.updateSellingPlanInput();
        } else {
          // For complex products, also set the selling plan on load
          this.updateSellingPlanInput();
        }
      } else {
        console.warn('No selling plans found for this variant');
      }
    }
  }

  detectComplexProduct() {
    // A complex product has:
    // 1. Multiple variants (more than 1)
    // 2. Variants with "Servings" in their option values

    if (!this.product || !this.product.variants) {
      return false;
    }

    // Check if we have multiple variants
    if (this.product.variants.length <= 1) {
      return false;
    }

    // Check if variants use "Servings" option (like "30 Servings", "60 Servings")
    const hasServingsOption = this.product.variants.some(variant => {
      return variant.option_values.some(option => {
        return option.label && option.label.includes('Servings');
      });
    });

    return hasServingsOption;
  }

  updateSellingPlans() {
    // For products with frequency-based variants (30/60/90/120 servings = 30/60/90/120 days),
    // we need to use ALL selling plans from the product, not just current variant's allocations.
    // Each frequency corresponds to a different variant.

    if (this.product && this.product.selling_plans) {
      this.sellingPlans = this.product.selling_plans.map(plan => {
        // Find the variant that matches this frequency (e.g., 30 days = 30 Servings variant)
        const matchingVariant = this.findVariantByFrequency(plan.order_interval_frequency);

        // Find the allocation for this plan on the matching variant
        let discountedPrice = null;
        if (matchingVariant) {
          const allocation = matchingVariant.selling_plan_allocations.find(
            a => a.selling_plan_id === plan.selling_plan_id
          );
          discountedPrice = allocation?.discounted_price;
        }

        // If no matching variant found, calculate discounted price from current variant
        if (!discountedPrice && this.currentVariant) {
          const basePrice = parseFloat(this.currentVariant.prices.unit_price);
          const discount = plan.price_adjustments_value || 0;
          discountedPrice = (basePrice * (1 - discount / 100)).toFixed(2);
        }

        return {
          selling_plan_id: plan.selling_plan_id,
          selling_plan_name: plan.selling_plan_name,
          order_interval_frequency: plan.order_interval_frequency,
          order_interval_unit: plan.order_interval_unit,
          discounted_price: discountedPrice,
          price_adjustments_value: plan.price_adjustments_value
        };
      });

      // Update frequency buttons with actual plan data
      this.updateFrequencyButtons();
    }
  }

  findVariantByFrequency(frequency) {
    // Find variant where servings match frequency (30 days = 30 Servings)
    const targetServings = `${frequency} Servings`;
    return this.product.variants.find(variant => {
      return variant.option_values.some(option => option.label === targetServings);
    });
  }

  updateFrequencyButtons() {
    // Map selling plans to frequency buttons (subscription section only)
    const frequencyButtons = this.querySelectorAll('.subscription-widget__frequencies .subscription-widget__frequency-btn');

    this.sellingPlans.forEach((plan, index) => {
      if (frequencyButtons[index]) {
        const button = frequencyButtons[index];
        button.setAttribute('data-frequency', plan.order_interval_frequency);
        button.setAttribute('data-plan-id', plan.selling_plan_id);
        button.setAttribute('data-plan-name', plan.selling_plan_name);
        button.textContent = `${plan.order_interval_frequency} days`;

        // Show the button if it was hidden
        button.style.display = 'block';
      }
    });

    // Hide buttons that don't have matching plans
    for (let i = this.sellingPlans.length; i < frequencyButtons.length; i++) {
      if (frequencyButtons[i]) {
        frequencyButtons[i].style.display = 'none';
      }
    }
  }

  updateOnetimeFrequencyButtons() {
    // For one-time purchases, mirror the subscription frequency options (30/60/90/120 days)
    // These correspond to the serving sizes (30/60/90/120 servings)
    const onetimeButtons = this.querySelectorAll('.subscription-widget__onetime-frequencies .subscription-widget__frequency-btn');

    // Use the same frequencies as subscription plans
    this.sellingPlans.forEach((plan, index) => {
      if (onetimeButtons[index]) {
        const button = onetimeButtons[index];
        button.setAttribute('data-frequency', plan.order_interval_frequency);
        button.textContent = `${plan.order_interval_frequency} days`;

        // Show the button
        button.style.display = 'block';
      }
    });

    // Hide buttons that don't have matching plans
    for (let i = this.sellingPlans.length; i < onetimeButtons.length; i++) {
      if (onetimeButtons[i]) {
        onetimeButtons[i].style.display = 'none';
      }
    }
  }

  updateSubscriptionPrice() {
    const priceElement = this.querySelector('.subscription-widget__subscription-price');

    if (!priceElement) return;

    // Find the selling plan for the selected frequency
    const matchingPlan = this.sellingPlans.find(plan => {
      return plan.order_interval_frequency === this.selectedFrequency;
    });

    if (matchingPlan && matchingPlan.discounted_price) {
      priceElement.textContent = `$${matchingPlan.discounted_price}`;
    } else if (this.currentVariant) {
      // Fallback to variant price if no matching plan found
      const subscriptionPrice = this.currentVariant.prices.discounted_price || this.currentVariant.prices.unit_price;
      priceElement.textContent = `$${subscriptionPrice}`;
    }
  }

  updateSubscriptionPriceForFrequency() {
    const priceElement = this.querySelector('.subscription-widget__subscription-price');

    if (!priceElement) return;

    // Find the selling plan for the selected frequency
    const matchingPlan = this.sellingPlans.find(plan => {
      return plan.order_interval_frequency === this.selectedFrequency;
    });

    if (matchingPlan && matchingPlan.discounted_price) {
      priceElement.textContent = `$${matchingPlan.discounted_price}`;
    }
  }

  updateOnetimePrice() {
    const priceElement = this.querySelector('.subscription-widget__onetime-price');

    if (!priceElement) return;

    // For products with frequency-based variants, get price from the variant matching selected frequency
    const matchingVariant = this.findVariantByFrequency(this.selectedFrequency);

    if (matchingVariant) {
      priceElement.textContent = `$${matchingVariant.prices.unit_price}`;
    } else if (this.currentVariant) {
      // Fallback to current variant price
      priceElement.textContent = `$${this.currentVariant.prices.unit_price}`;
    }
  }

  updateActiveFrequencyButton() {
    // Update active state on frequency buttons
    this.querySelectorAll('.subscription-widget__frequency-btn').forEach(btn => {
      const btnFrequency = Number(btn.getAttribute('data-frequency'));
      if (btnFrequency === this.selectedFrequency) {
        btn.classList.add('active');
        btn.setAttribute('aria-checked', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-checked', 'false');
      }
    });
  }

  attachEventListeners() {
    // Subscription frequency button clicks
    const subscriptionFrequencyButtons = this.querySelectorAll('.subscription-widget__frequencies .subscription-widget__frequency-btn');
    subscriptionFrequencyButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleFrequencyChange(e));
    });

    // One-time frequency button clicks
    const onetimeFrequencyButtons = this.querySelectorAll('.subscription-widget__onetime-frequencies .subscription-widget__frequency-btn');
    onetimeFrequencyButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleFrequencyChange(e));
    });

    // Purchase option radios
    const onetimeRadio = this.querySelector('.subscription-widget__onetime-radio');
    const subscriptionRadio = this.querySelector('.subscription-widget__subscription-radio');

    if (onetimeRadio) {
      onetimeRadio.addEventListener('change', () => this.handlePurchaseOptionChange('onetime'));
    }

    if (subscriptionRadio) {
      subscriptionRadio.addEventListener('change', () => this.handlePurchaseOptionChange('subscription'));
    }

    // Listen for variant changes from product variant picker
    const variantIdInput = document.querySelector('.product-variant-id');
    if (variantIdInput) {
      variantIdInput.addEventListener('change', () => {
        this.handleVariantChange({ id: Number(variantIdInput.value) });
      });
    }
  }

  changeVariantByFrequency(frequency) {
    // Map frequency (days) to servings - 30 days = 30 servings, 60 days = 60 servings, etc.
    const targetServings = `${frequency} Servings`;

    // Find the variant that matches this serving size
    const targetVariant = this.product.variants.find(variant => {
      return variant.option_values.some(option => option.label === targetServings);
    });

    if (targetVariant && targetVariant.id !== this.variantID) {
      // Update the variant ID
      this.variantID = targetVariant.id;
      this.currentVariant = targetVariant;

      // Update the product form variant input
      const variantInput = document.querySelector('.product-form form input[name="id"]');
      if (variantInput) {
        variantInput.value = targetVariant.id;

        // Trigger change event so other scripts know the variant changed
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update the variant picker buttons if they exist
      const variantButtons = document.querySelectorAll('.product-form__input input[type="radio"]');
      variantButtons.forEach(radio => {
        if (radio.value === String(targetVariant.id)) {
          radio.checked = true;
        } else {
          radio.checked = false;
        }
      });

      // Update selling plans for the new variant
      this.updateSellingPlans();

      // Update prices
      this.updateSubscriptionPrice();
      this.updateOnetimePrice();
    }
  }

  handleFrequencyChange(event) {
    const button = event.currentTarget;
    const frequency = Number(button.getAttribute('data-frequency'));
    const isOnetimeButton = button.closest('.subscription-widget__onetime-frequencies');

    if (isOnetimeButton) {
      // Handle one-time frequency button clicks
      // Update active state for one-time buttons only
      this.querySelectorAll('.subscription-widget__onetime-frequencies .subscription-widget__frequency-btn').forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-checked', 'false');
      });

      button.classList.add('active');
      button.setAttribute('aria-checked', 'true');

      this.selectedFrequency = frequency;

      // Change variant based on frequency (30 days = 30 servings, etc.)
      this.changeVariantByFrequency(frequency);

      // IMPORTANT: Clear selling_plan after variant change for one-time purchase
      // This ensures the old Recharge widget doesn't re-set it
      this.clearSellingPlanForOnetime();

      // Update one-time price
      this.updateOnetimePrice();

      // Update main submit button with price
      this.updateMainSubmitButton();
    } else {
      // Handle subscription frequency button clicks
      // Update active state for subscription buttons only
      this.querySelectorAll('.subscription-widget__frequencies .subscription-widget__frequency-btn').forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-checked', 'false');
      });

      button.classList.add('active');
      button.setAttribute('aria-checked', 'true');

      this.selectedFrequency = frequency;

      // Change variant based on frequency (30 days = 30 servings, etc.)
      this.changeVariantByFrequency(frequency);

      // Update the selling plan input to send correct subscription to cart
      this.updateSellingPlanInput();

      // Update the subscription price display based on selected frequency
      this.updateSubscriptionPriceForFrequency();

      // Update main submit button with price
      this.updateMainSubmitButton();
    }
  }

  handleTabChange(event) {
    const button = event.currentTarget;
    const tab = button.getAttribute('data-tab');

    // These are action buttons, not toggles
    // You can add modal/popup logic here when ready
    console.log('Tab clicked:', tab);

    // TODO: Open modal for Free Delivery or Subscribe Perks info
  }

  handlePurchaseOptionChange(option) {
    const subscriptionSection = this.querySelector('.subscription-widget__subscription-section');
    const onetimeSection = this.querySelector('.subscription-widget__onetime');

    if (option === 'onetime') {
      // Clear selling plan for one-time purchase
      this.clearSellingPlanForOnetime();

      // Accordion: collapse subscription, expand one-time
      if (subscriptionSection) {
        subscriptionSection.classList.remove('expanded');
        subscriptionSection.classList.add('dimmed');
      }
      if (onetimeSection) {
        onetimeSection.classList.add('expanded');
        onetimeSection.classList.add('active');
      }

      // Sync one-time button active state to match current variant
      this.syncOnetimeButtonState();

      // Update main submit button with one-time price
      this.updateMainSubmitButton();
    } else if (option === 'subscription') {
      // Set selling plan for subscription
      this.updateSellingPlanInput();

      // Accordion: expand subscription, collapse one-time
      if (subscriptionSection) {
        subscriptionSection.classList.add('expanded');
        subscriptionSection.classList.remove('dimmed');
      }
      if (onetimeSection) {
        onetimeSection.classList.remove('expanded');
        onetimeSection.classList.remove('active');
      }

      // Update main submit button with subscription price
      this.updateMainSubmitButton();
    }
  }

  syncOnetimeButtonState() {
    // When switching to one-time, make sure the active button matches the current frequency
    const onetimeButtons = this.querySelectorAll('.subscription-widget__onetime-frequencies .subscription-widget__frequency-btn');

    onetimeButtons.forEach(btn => {
      const btnFrequency = Number(btn.getAttribute('data-frequency'));
      if (btnFrequency === this.selectedFrequency) {
        btn.classList.add('active');
        btn.setAttribute('aria-checked', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-checked', 'false');
      }
    });
  }

  handleVariantChange(variant) {
    if (!variant || !variant.id) return;

    this.variantID = Number(variant.id);

    // Check if new variant is excluded
    if (this.isVariantExcluded(this.variantID)) {
      // Hide widget for excluded variants
      this.hideWidget();
      return;
    }

    // Show widget and update
    const widgetContainer = this.querySelector('.subscription-widget');
    if (widgetContainer) {
      widgetContainer.classList.remove('hidden');
    }

    // Update current variant
    this.currentVariant = this.product.variants.find(v => v.id === this.variantID);

    this.updateSellingPlans();
    this.updateSubscriptionPrice();
    this.updateOnetimePrice();
    this.updateSellingPlanInput();

    // Update main submit button with price
    this.updateMainSubmitButton();
  }

  updateSellingPlanInput() {
    // Find the selling plan that matches the selected frequency
    const matchingPlan = this.sellingPlans.find(plan => {
      return plan.order_interval_frequency === this.selectedFrequency;
    });

    const productFormInput = this.getAssociatedSellingPlanInput();

    if (matchingPlan && productFormInput) {
      productFormInput.value = matchingPlan.selling_plan_id;
      productFormInput.setAttribute('value', matchingPlan.selling_plan_id);
      // Dispatch change event so cart apps like UpCart detect the change
      productFormInput.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('Set selling_plan to:', matchingPlan.selling_plan_id);
    } else if (!productFormInput) {
      console.warn('Could not find selling_plan input to update');
    }
  }

  /**
   * Clear selling_plan for one-time purchases and disable old Recharge inputs
   * Called after variant changes to ensure one-time purchase stays one-time
   */
  clearSellingPlanForOnetime() {
    // Clear the main form's selling_plan input
    const productFormInput = this.getAssociatedSellingPlanInput();
    if (productFormInput) {
      productFormInput.value = '';
      productFormInput.setAttribute('value', '');
      productFormInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Re-disable old Recharge widget inputs (they may have been re-enabled on variant change)
    const rechargeInputs = document.querySelectorAll(
      'recharge-widget input, recharge-widget select, ' +
      '.rc-widget input, .rc-widget select, ' +
      '[class*="rc-widget"] input, [class*="rc-widget"] select, ' +
      'input[name*="selling_plan_"], select[name*="rc_selling_plan"]'
    );
    rechargeInputs.forEach(input => {
      input.disabled = true;
      input.removeAttribute('name');
      input.value = '';
    });

    console.log('Cleared selling_plan for one-time purchase');
  }

  /**
   * Find the selling_plan input associated with this specific widget's product
   * This ensures we update the correct form when multiple products are on the page
   */
  getAssociatedSellingPlanInput() {
    // Find the selling_plan input that's inside a form (not inside the widget itself)
    // The widget's input has class 'subscription-widget__selling-plan-input'
    // The form's input does not have this class

    // First, try to find within the same section
    const section = this.closest('.shopify-section, section, [class*="section"]');
    if (section) {
      // Find the form's selling_plan input (exclude the widget's own input)
      const inputs = section.querySelectorAll('form input[name="selling_plan"]');
      for (const input of inputs) {
        // Skip if it's the widget's own input
        if (!input.classList.contains('subscription-widget__selling-plan-input')) {
          return input;
        }
      }
    }

    // Fallback: find any form's selling_plan input that's not inside a subscription-widget
    const allInputs = document.querySelectorAll('form input[name="selling_plan"]');
    for (const input of allInputs) {
      if (!input.closest('subscription-widget')) {
        return input;
      }
    }

    return null;
  }

  /**
   * Update the main product-form__submit button with the current price
   */
  updateMainSubmitButton() {
    const submitButton = document.querySelector('.product-form__submit');
    const buttonText = submitButton?.querySelector('.product-form__submit-text');

    if (!buttonText || submitButton?.disabled) return;

    // Determine which price to show based on purchase option
    const subscriptionRadio = this.querySelector('.subscription-widget__subscription-radio');
    const isSubscription = subscriptionRadio?.checked;

    let price;
    if (isSubscription) {
      const subscriptionPrice = this.querySelector('.subscription-widget__subscription-price');
      price = subscriptionPrice?.textContent?.trim();
    } else {
      const onetimePrice = this.querySelector('.subscription-widget__onetime-price');
      price = onetimePrice?.textContent?.trim();
    }

    if (price) {
      buttonText.textContent = `Add to Cart - ${price}`;
    }
  }
}

// Register custom element
if (!customElements.get('subscription-widget')) {
  customElements.define('subscription-widget', SubscriptionWidget);
}
